import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  CurrencyDollarIcon, 
  EnvelopeIcon, 
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { calculateCost } from '../utils/costCalculator';

function Dashboard() {
  const { isAuthenticated, user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [totalStats, setTotalStats] = useState({
    totalUsed: 0,
    totalCost: 0,
    totalRemaining: 0,
    totalLimit: 0
  });

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  useEffect(() => {
    if (isAuthenticated) {
      loadAccountsData();
    }
  }, [isAuthenticated]);

  const loadAccountsData = async () => {
    console.log('isAuthenticated:', isAuthenticated);
    console.log('user:', user);
    console.log('axios headers:', axios.defaults.headers.common);
    
    if (!isAuthenticated) {
      console.log('Usuario no autenticado, no se cargan las cuentas');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts.php');
      
      if (response.data.success) {
        setAccounts(response.data.data.accounts || []);
        calculateTotalStats(response.data.data.accounts || []);
        setLastUpdate(new Date());
      } else {
        toast.error('Error al cargar los datos de las cuentas');
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Error de conexión al cargar las cuentas');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalStats = (accountsData) => {
    const stats = accountsData.reduce((acc, account) => {
      const used = parseInt(account.used) || 0;
      const calculatedCost = calculateCost(used);
      
      acc.totalUsed += used;
      acc.totalCost += calculatedCost;
      acc.totalRemaining += parseInt(account.remain) || 0;
      acc.totalLimit += parseInt(account.total) || 0;
      return acc;
    }, {
      totalUsed: 0,
      totalCost: 0,
      totalRemaining: 0,
      totalLimit: 0
    });
    
    setTotalStats(stats);
  };

  const refreshAccount = async (accountId) => {
    try {
      const response = await axios.post('/api/refresh-account.php', { accountId });
      
      if (response.data.success && response.data.data && response.data.data.account) {
        // Actualizar la cuenta específica en el estado
        setAccounts(prev => prev.map(acc => 
          acc.id === accountId ? { ...acc, ...response.data.data.account } : acc
        ));
        toast.success(`Datos actualizados para ${response.data.data.account.name}`);
        console.log('Cuenta actualizada exitosamente:', response.data.data.account.name);
      } else {
        const errorMessage = response.data.message || 'Error al actualizar la cuenta';
        toast.error(errorMessage);
        console.error('API Error:', response.data);
      }
    } catch (error) {
      console.error('Error refreshing account:', error);
      const errorMessage = error.response?.data?.message || 'Error de conexión al actualizar la cuenta';
      toast.error(errorMessage);
    }
  };

  const refreshAllAccounts = async () => {
    await loadAccountsData();
    toast.success('Todas las cuentas han sido actualizadas');
  };

  const getUsagePercentage = (used, total) => {
    if (!total) return 0;
    return Math.round((used / total) * 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-danger-500';
    if (percentage >= 75) return 'bg-warning-500';
    if (percentage >= 50) return 'bg-primary-500';
    return 'bg-success-500';
  };

  const chartData = (accounts || []).map(account => {
    const used = parseInt(account.used) || 0;
    return {
      name: account.name.length > 15 ? account.name.substring(0, 15) + '...' : account.name,
      usado: used,
      restante: parseInt(account.remain) || 0,
      costo: calculateCost(used)
    };
  });

  const pieData = (accounts || []).map((account, index) => ({
    name: account.name,
    value: parseInt(account.used) || 0,
    color: COLORS[index % COLORS.length]
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con botón de actualización */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-gray-600 mt-1">
            {lastUpdate && `Última actualización: ${lastUpdate.toLocaleString()}`}
          </p>
        </div>
        <button
          onClick={refreshAllAccounts}
          className="btn btn-primary flex items-center space-x-2"
        >
          <ArrowPathIcon className="h-5 w-5" />
          <span>Actualizar Todo</span>
        </button>
      </div>

      {/* Tarjetas de estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EnvelopeIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Emails Enviados</dt>
                  <dd className="text-2xl font-bold text-gray-900">
                    {totalStats.totalUsed.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-success-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Costo Total</dt>
                  <dd className="text-2xl font-bold text-gray-900">
                    ${totalStats.totalCost.toFixed(2)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-warning-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Emails Restantes</dt>
                  <dd className="text-2xl font-bold text-gray-900">
                    {totalStats.totalRemaining.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-secondary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Límite Total</dt>
                  <dd className="text-2xl font-bold text-gray-900">
                    {totalStats.totalLimit.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de barras */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Uso por Cuenta</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="usado" fill="#3B82F6" name="Usado" />
                <Bar dataKey="restante" fill="#10B981" name="Restante" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico circular */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Distribución de Uso</h3>
          </div>
          <div className="card-body">
            <div className="flex flex-col lg:flex-row items-center">
              <div className="w-full lg:w-2/3">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value.toLocaleString(), 'Emails enviados']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/3 mt-4 lg:mt-0 lg:pl-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Leyenda</h4>
                  {pieData.map((entry, index) => {
                    const percentage = ((entry.value / pieData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
                    return (
                      <div key={index} className="flex items-center text-sm">
                        <div 
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                          style={{ backgroundColor: entry.color }}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium text-gray-900" title={entry.name}>
                            {entry.name.length > 20 ? entry.name.substring(0, 20) + '...' : entry.name}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {entry.value.toLocaleString()} ({percentage}%)
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de cuentas */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Detalle de Cuentas</h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Cuenta</th>
                  <th>Límite</th>
                  <th>Usado</th>
                  <th>Restante</th>
                  <th>Uso %</th>
                  <th>Costo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(accounts || []).map((account) => {
                  const usagePercentage = getUsagePercentage(account.used, account.total);
                  return (
                    <tr key={account.id}>
                      <td className="font-medium">{account.name}</td>
                      <td>{parseInt(account.total).toLocaleString()}</td>
                      <td>{parseInt(account.used).toLocaleString()}</td>
                      <td>{parseInt(account.remain).toLocaleString()}</td>
                      <td>
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(usagePercentage)}`}
                              style={{ width: `${usagePercentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">{usagePercentage}%</span>
                        </div>
                      </td>
                      <td className="font-medium">${calculateCost(parseInt(account.used) || 0).toFixed(2)}</td>
                      <td>
                        {usagePercentage >= 90 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-800">
                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            Crítico
                          </span>
                        ) : usagePercentage >= 75 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800">
                            Advertencia
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                            Normal
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => refreshAccount(account.id)}
                          className="btn btn-secondary btn-sm"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;